import { LanguagesEnum } from '@/api/enums/languages.enum';
import { handleAxiosError } from '@/api/helpers/api-errors.helper';
import { Account, AccountAD } from '@/client/accounts';
import {
  ADEntityField,
  AzureAdField,
} from '@/client/accounts/types/account-ad-config.type';
import { UserManagementFormIndexValueMapping } from '@/client/accounts/types/account-user-management.type';
import { LoadingStateType } from '@/common/constants';
import { UserManagementGroupSelection } from '@/components/accounts/UserManagementGroupSelection';
import { FormDirtyTracker, FormikDropdown } from '@/components/form';
import { useSaveADConfig } from '@/hooks/query/account-ad-sync.hooks';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useToast } from '@/hooks/useToast';
import { AppButton } from '@/ui/buttons';
import { CheckboxesContainer } from '@/ui/checkboxes-container';
import { AppChip } from '@/ui/chip';
import { FlexContainer, FormContainer } from '@/ui/styled-ui';
import { AppSwitch } from '@/ui/switch/AppSwitch';
import { SimpleLockIcon } from '@/utils/helpers/icons.helper';
import { PostHogFeatureFlags } from '@/utils/posthog/posthog.events';
import { AxiosError } from 'axios';
import { Field, Form, Formik } from 'formik';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Tooltip } from 'primereact/tooltip';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';
import { v4 } from 'uuid';

type FieldKey =
  | 'username'
  | 'email'
  | 'firstName'
  | 'lastName'
  | 'companyName'
  | 'department'
  | 'jobTitle'
  | 'manager'
  | 'officeLocation'
  | 'country'
  | 'mobilePhone'
  | 'uiLanguage';

type FieldMappingRow = {
  key: FieldKey;
  label: string;
  locked?: boolean;
  enabledByDefault?: boolean;
  attributeDefault: AzureAdField;
};

type FieldMappingValues = Record<
  FieldKey,
  {
    enabled: boolean;
    attribute: AzureAdField;
  }
>;

type AdGroupMappingFormValues = {
  active: boolean;
  mapping: UserManagementFormIndexValueMapping;
};

const CUSTOM_ATTRIBUTE = '__custom__';

const StyledTable = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--default-padding);
  margin-bottom: var(--big-padding);
`;

const StyledSyncSettingsPanel = styled.div`
  background: var(--beige-main);
  border-radius: var(--semi-medium-border-radius);
  padding: var(--medium-big-padding);
`;

const StyledSyncSettingsInner = styled.div``;

const StyledCheckboxesContainer = styled(CheckboxesContainer)`
  border-radius: var(--box-border-radius);
  border: 1px solid var(--gray-border-lighter);
`;

const StyledCardHeader = styled(FlexContainer)`
  width: 100%;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--default-padding);
`;

const StyledHeaderActions = styled(FlexContainer)`
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
`;

const StyledCollapseButton = styled.button`
  border: none;
  border-radius: 999px;
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: transparent;

  i {
    color: var(--gray-darker);
    font-size: var(--small-font-size);
  }
`;

const StyledTitle = styled.div`
  font-size: var(--medium-font-size);
  font-weight: 600;
  color: var(--black-main);
`;

const StyledSubtitle = styled.div`
  margin-top: 6px;
  font-size: var(--small-font-size);
  color: var(--black-main);
  line-height: 150%;
`;

const StyledPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  width: fit-content;

  font-weight: 500;
  background: var(--platinum-main);
  color: var(--gray-darkest);
`;

const StyledCardRowTitle = styled.div`
  font-size: var(--small-font-size);
  font-weight: 600;
  color: var(--black-main);
`;

const StyledUserStatusInnerCard = styled.div`
  width: 100%;
  background: var(--white-main);
  border: 1px solid var(--gray-border-lighter);
  border-radius: var(--xsmall-border-radius);
  padding: var(--default-padding);
`;

const StyledUserStatusSectionHeader = styled(StyledCardHeader)`
  margin-bottom: var(--default-padding);
`;

const StyledUserStatusSectionSubtitle = styled(StyledSubtitle)`
  margin-top: var(--xsmall-padding);
  color: var(--black-main);
`;

const StyledUserStatusDescription = styled.div`
  margin-top: var(--small-padding);
  font-size: var(--small-font-size);
  line-height: 1.5;
  color: var(--black-main);
`;

const StyledUserStatusDescriptionLine = styled.div`
  &:not(:first-child) {
    margin-top: var(--xxsmall-padding);
  }
`;

const StyledUserStatusTitleRow = styled(FlexContainer)`
  width: 100%;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--default-padding);
`;

const StyledUserStatusTitleCol = styled.div`
  flex: 1;
  min-width: 0;
`;

const StyledSyncNowRow = styled(FlexContainer)`
  margin-top: var(--default-padding);
  justify-content: flex-start;
  align-items: center;
`;

const StyledFullWidthAddButton = styled.button`
  display: flex;
  width: 100%;
  justify-content: center;
  color: var(--gray-darker) !important;
  min-height: 56px;
  border-radius: var(--xsmall-border-radius);
  border: 1px solid var(--gray-border-lighter);
  display: flex;
  padding: var(--small-padding);
  justify-content: center;
  align-items: center;
  gap: 6px;
  align-self: stretch;
  margin-top: var(--medium-big-padding);
  cursor: pointer;
  background: var(--beige-main);
`;

const StyledForm = styled(Form)`
  overflow: hidden;
  border: 1px solid var(--gray-dark);
  border-radius: var(--xsmall-border-radius) var(--xsmall-border-radius) 0 0;
  margin-top: var(--default-padding);
`;

const StyledHeaderRow = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr 140px;
  font-size: var(--small-font-size);
  font-weight: 700;

  > div {
    padding: var(--medium-padding);
    border-right: 1px solid var(--gray-dark);
    height: 100%;
  }
`;

const StyledRow = styled.div<{ isLocked?: boolean }>`
  display: grid;
  grid-template-columns: 240px 1fr 140px;
  align-items: center;
  border-bottom: 1px solid var(--gray-pale);

  > div {
    padding: var(--medium-padding);
    border-top: 1px solid var(--gray-dark);
    height: 100%;
  }
`;

const StyledFieldLabel = styled.div<{ $locked?: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--xsmall-padding);
  font-size: var(--small-font-size);
  font-weight: 500;
  color: var(--black-main);
  opacity: ${({ $locked }) => ($locked ? 0.55 : 1)};
`;

type EntraIdSyncSettingsProps = {
  account: Account;
  disabled?: boolean;
  isDemoAccount?: boolean;
  adConfig?: AccountAD | null;
  onSync?: () => void;
  syncState?: LoadingStateType;
  onDirtyChange?: (dirty: boolean) => void;
  saveRef?: React.MutableRefObject<(() => Promise<boolean | void>) | null>;
};

export const EntraIdSyncSettings: React.FC<EntraIdSyncSettingsProps> = ({
  account,
  disabled = false,
  isDemoAccount = false,
  adConfig,
  onSync,
  syncState,
  onDirtyChange,
  saveRef,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [isPropertyEditing, setIsPropertyEditing] = useState(false);
  const [customDropdownRows, setCustomDropdownRows] = useState<
    Partial<Record<FieldKey, boolean>>
  >({});
  const [isSyncSettingsOpen, setIsSyncSettingsOpen] = useState(true);
  const [isAdGroupMappingOpen, setIsAdGroupMappingOpen] = useState(true);
  const fieldMappingFormikRef = useRef<any>(null);
  const adGroupFormikRef = useRef<any>(null);
  const { canUseFeature, accountCanUseFeature } = useFeatureFlag();
  const isUserStatusLockEnabled =
    canUseFeature(PostHogFeatureFlags.ENTRA_ID_FIELD_MAPPING) ||
    accountCanUseFeature(
      PostHogFeatureFlags.ENTRA_ID_FIELD_MAPPING,
      account?.featureFlagAbility,
    );

  const attributeOptions: AzureAdField[] = [
    AzureAdField.USER_PRINCIPAL_NAME,
    AzureAdField.MAIL,
    AzureAdField.GIVEN_NAME,
    AzureAdField.SURNAME,
    AzureAdField.DISPLAY_NAME,
    AzureAdField.COMPANY_NAME,
    AzureAdField.DEPARTMENT,
    AzureAdField.JOB_TITLE,
    AzureAdField.MANAGER,
    AzureAdField.OFFICE_LOCATION,
    AzureAdField.COUNTRY,
    AzureAdField.MOBILE_PHONE,
    AzureAdField.PREFERRED_LANGUAGE,
    AzureAdField.ACCOUNT_ENABLED,
    AzureAdField.EMPLOYEE_ID,
    AzureAdField.EMPLOYEE_TYPE,
    AzureAdField.CITY,
    AzureAdField.STATE,
    AzureAdField.STREET_ADDRESS,
    AzureAdField.POSTAL_CODE,
    AzureAdField.USAGE_LOCATION,
    AzureAdField.BUSINESS_PHONES,
    AzureAdField.FAX_NUMBER,
    AzureAdField.MAIL_NICKNAME,
    AzureAdField.LOCALE,
    AzureAdField.USER_TYPE,
    AzureAdField.CREATED_DATE_TIME,
    AzureAdField.LAST_PASSWORD_CHANGE_DATE_TIME,
    AzureAdField.PASSWORD_POLICIES,
  ];

  const attributeValueTemplate = (option: any) =>
    option?.label ? <StyledPill>{option.label}</StyledPill> : null;

  const attributeItemTemplate = (option: any) =>
    option?.label ? <span>{option.label}</span> : null;

  const rows: Array<{ section: string; items: FieldMappingRow[] }> = [
    {
      section: t('azure.entra.fieldMapping.section.core'),
      items: [
        {
          key: 'username',
          label: t('azure.entra.fieldMapping.field.username'),
          locked: true,
          enabledByDefault: true,
          attributeDefault: AzureAdField.USER_PRINCIPAL_NAME,
        },
        {
          key: 'email',
          label: t('azure.entra.fieldMapping.field.email'),
          locked: true,
          enabledByDefault: true,
          attributeDefault: AzureAdField.MAIL,
        },
        {
          key: 'firstName',
          label: t('azure.entra.fieldMapping.field.firstName'),
          enabledByDefault: true,
          attributeDefault: AzureAdField.GIVEN_NAME,
        },
        {
          key: 'lastName',
          label: t('azure.entra.fieldMapping.field.lastName'),
          enabledByDefault: true,
          attributeDefault: AzureAdField.SURNAME,
        },
      ],
    },
    {
      section: t('azure.entra.fieldMapping.section.organizational'),
      items: [
        {
          key: 'companyName',
          label: t('azure.entra.fieldMapping.field.companyName'),
          enabledByDefault: true,
          attributeDefault: AzureAdField.COMPANY_NAME,
        },
        {
          key: 'department',
          label: t('azure.entra.fieldMapping.field.department'),
          enabledByDefault: true,
          attributeDefault: AzureAdField.DEPARTMENT,
        },
        {
          key: 'jobTitle',
          label: t('azure.entra.fieldMapping.field.jobTitle'),
          enabledByDefault: true,
          attributeDefault: AzureAdField.JOB_TITLE,
        },
        {
          key: 'manager',
          label: t('azure.entra.fieldMapping.field.manager'),
          enabledByDefault: true,
          attributeDefault: AzureAdField.MANAGER,
        },
        {
          key: 'officeLocation',
          label: t('azure.entra.fieldMapping.field.officeLocation'),
          enabledByDefault: true,
          attributeDefault: AzureAdField.OFFICE_LOCATION,
        },
        {
          key: 'country',
          label: t('azure.entra.fieldMapping.field.country'),
          enabledByDefault: true,
          attributeDefault: AzureAdField.COUNTRY,
        },
      ],
    },
    {
      section: t('azure.entra.fieldMapping.section.contact'),
      items: [
        {
          key: 'mobilePhone',
          label: t('azure.entra.fieldMapping.field.mobilePhone'),
          enabledByDefault: true,
          attributeDefault: AzureAdField.MOBILE_PHONE,
        },
      ],
    },
    {
      section: t('azure.entra.fieldMapping.section.language'),
      items: [
        {
          key: 'uiLanguage',
          label: t('azure.entra.fieldMapping.field.uiLanguage'),
          enabledByDefault: true,
          attributeDefault: AzureAdField.PREFERRED_LANGUAGE,
        },
      ],
    },
  ];

  const flatRows = rows.flatMap((group) => group.items);

  const initialValues: FieldMappingValues = useMemo(() => {
    const base: Partial<FieldMappingValues> = {};
    rows.forEach((group) => {
      group.items.forEach((row) => {
        base[row.key] = {
          enabled: !!row.enabledByDefault,
          attribute: row.attributeDefault,
        };
      });
    });
    return base as FieldMappingValues;
  }, [rows]);

  const [savedValues, setSavedValues] =
    useState<FieldMappingValues>(initialValues);

  const [userStatusSyncEnabled, setUserStatusSyncEnabled] = useState(
    adConfig?.config?.checkActiveDirectoryEnabledStatus ?? true,
  );

  useEffect(() => {
    if (adConfig?.config?.checkActiveDirectoryEnabledStatus === undefined)
      return;
    setUserStatusSyncEnabled(adConfig.config.checkActiveDirectoryEnabledStatus);
  }, [adConfig?.config?.checkActiveDirectoryEnabledStatus]);

  const [fieldMappingDirty, setFieldMappingDirty] = useState(false);
  const [adGroupDirty, setAdGroupDirty] = useState(false);
  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;

  const userStatusDirty =
    userStatusSyncEnabled !==
    (adConfig?.config?.checkActiveDirectoryEnabledStatus ?? true);

  useEffect(() => {
    onDirtyChangeRef.current?.(
      fieldMappingDirty || adGroupDirty || userStatusDirty,
    );
  }, [fieldMappingDirty, adGroupDirty, userStatusDirty]);

  const initialAdGroupValues: AdGroupMappingFormValues = useMemo(
    () => ({
      active: false,
      mapping: {
        [v4()]: {
          groupId: '',
          account,
          branches: [],
          groups: [],
          language: LanguagesEnum.EN,
        },
      },
    }),
    [account],
  );

  const [savedAdGroupValues, setSavedAdGroupValues] =
    useState<AdGroupMappingFormValues>(initialAdGroupValues);

  useEffect(() => {
    if (!adConfig?.attributesMap || !adConfig.attributesMap.length) return;

    const next: FieldMappingValues = { ...savedValues };

    const reverseTargetFieldMap: Partial<Record<ADEntityField, FieldKey>> = {
      [ADEntityField.FIRST_NAME]: 'firstName',
      [ADEntityField.LAST_NAME]: 'lastName',
      [ADEntityField.LANGUAGE]: 'uiLanguage',
      [ADEntityField.COMPANY_DATA_COMPANY_NAME]: 'companyName',
      [ADEntityField.COMPANY_DATA_DEPARTMENT]: 'department',
      [ADEntityField.COMPANY_DATA_JOB_TITLE]: 'jobTitle',
      [ADEntityField.COMPANY_DATA_MANAGER]: 'manager',
      [ADEntityField.COMPANY_DATA_OFFICE_LOCATION]: 'officeLocation',
      [ADEntityField.COMPANY_DATA_COUNTRY]: 'country',
      [ADEntityField.COMPANY_DATA_MOBILE_PHONE]: 'mobilePhone',
    };

    for (const attr of adConfig.attributesMap) {
      if (!attr.entityField) continue;

      const key = reverseTargetFieldMap[attr.entityField];
      if (!key) continue;

      next[key] = {
        enabled: !!attr.synced,
        attribute: attr.azureField,
      };
    }

    setSavedValues(next);
  }, [adConfig?.attributesMap]);

  useEffect(() => {
    if (!adConfig?.mapping || !adConfig.mapping.length) return;

    const mappingEntries: UserManagementFormIndexValueMapping = {};

    adConfig.mapping.forEach((m) => {
      const idx = v4();
      mappingEntries[idx] = {
        groupId: m.groupId,
        account,
        branches: m.branches ?? [],
        groups: m.groups ?? [],
        language: m.language ?? LanguagesEnum.EN,
      };
    });

    setSavedAdGroupValues({
      active: true,
      mapping: mappingEntries,
    });
  }, [adConfig?.mapping, account]);

  const enterPropertyEdit = () => {
    fieldMappingFormikRef.current?.resetForm?.();
    setIsPropertyEditing(true);
  };

  const savePropertyEdit = async () => {
    await fieldMappingFormikRef.current?.submitForm?.();
    setIsPropertyEditing(false);
  };

  const { create: saveAdConfig } = useSaveADConfig();

  const handleSaveIntegrationSettings = async () => {
    if (disabled || isDemoAccount || !account?.id) return;

    const fieldValues: FieldMappingValues =
      fieldMappingFormikRef.current?.values ?? savedValues;
    const adGroupValues: AdGroupMappingFormValues =
      adGroupFormikRef.current?.values ?? savedAdGroupValues;

    const attributesMap = flatRows.reduce<
      {
        azureField: AzureAdField;
        entityField: ADEntityField | null;
        synced: boolean;
      }[]
    >((acc, row) => {
      if (row.key === 'username' || row.key === 'email') {
        return acc;
      }

      const value = fieldValues[row.key];
      const azureField = value.attribute;
      if (!azureField) {
        return acc;
      }

      const synced = !!(row.locked || value.enabled);

      const targetFieldMap: Partial<Record<FieldKey, ADEntityField>> = {
        firstName: ADEntityField.FIRST_NAME,
        lastName: ADEntityField.LAST_NAME,
        uiLanguage: ADEntityField.LANGUAGE,
        companyName: ADEntityField.COMPANY_DATA_COMPANY_NAME,
        department: ADEntityField.COMPANY_DATA_DEPARTMENT,
        jobTitle: ADEntityField.COMPANY_DATA_JOB_TITLE,
        manager: ADEntityField.COMPANY_DATA_MANAGER,
        officeLocation: ADEntityField.COMPANY_DATA_OFFICE_LOCATION,
        country: ADEntityField.COMPANY_DATA_COUNTRY,
        mobilePhone: ADEntityField.COMPANY_DATA_MOBILE_PHONE,
      };

      const entityField = targetFieldMap[row.key] ?? null;

      acc.push({
        azureField,
        entityField,
        synced,
      });

      return acc;
    }, []);

    const mapping =
      adGroupValues.active && adGroupValues.mapping
        ? Object.values(adGroupValues.mapping).map((group) => ({
            groupId: group.groupId,
            branches: (group.branches ?? []).map(({ id }) => ({ id })),
            groups: (group.groups ?? []).map(({ id }) => ({ id })),
            language: group.language,
          }))
        : [];

    try {
      await saveAdConfig({
        accountId: account.id,
        updates: {
          active: true,
          mapping,
          attributesMap: canUseFeature(
            PostHogFeatureFlags.ENTRA_ID_FIELD_MAPPING,
          )
            ? attributesMap
            : undefined,
          ...(adConfig?.config && {
            config: {
              clientId: adConfig.config.clientId,
              tenantId: adConfig.config.tenantId,
              checkActiveDirectoryEnabledStatus: userStatusSyncEnabled,
            },
          }),
        },
      });

      setSavedValues(fieldValues);
      setSavedAdGroupValues(adGroupValues);
      queryClient.invalidateQueries(['accountAdConfig', account.id]);

      toast?.success(
        t('toast.success'),
        t('account.save.integration.settings'),
      );
      return true;
    } catch (error) {
      handleAxiosError(error as Error | AxiosError, toast);
      return false;
    }
  };

  const saveHandlerRef = useRef(handleSaveIntegrationSettings);
  saveHandlerRef.current = handleSaveIntegrationSettings;

  useEffect(() => {
    if (!saveRef) return;
    saveRef.current = () => saveHandlerRef.current();
    return () => {
      saveRef.current = null;
    };
  }, [saveRef]);

  return (
    <FormContainer width={100}>
      <StyledTable>
        {canUseFeature(PostHogFeatureFlags.ENTRA_ID_FIELD_MAPPING) && (
          <StyledSyncSettingsPanel>
            <StyledSyncSettingsInner>
              <StyledCardHeader>
                <div>
                  <StyledTitle>
                    {t('azure.entra.fieldMapping.syncSettingsTitle')}
                  </StyledTitle>
                  <StyledSubtitle>
                    {t('azure.entra.fieldMapping.description')}
                  </StyledSubtitle>
                </div>

                <StyledHeaderActions>
                  {isPropertyEditing ? (
                    <AppButton
                      type="outlined"
                      severity="no-background"
                      label={t('azure.entra.fieldMapping.finishMapping')}
                      onClick={savePropertyEdit}
                    />
                  ) : (
                    <AppButton
                      type="outlined"
                      severity="no-background"
                      label={t('azure.entra.fieldMapping.customizeMapping')}
                      onClick={enterPropertyEdit}
                    />
                  )}
                  <StyledCollapseButton
                    type="button"
                    aria-label={t(
                      'azure.entra.fieldMapping.syncSettingsToggleCollapse',
                    )}
                    onClick={() => setIsSyncSettingsOpen((v) => !v)}
                  >
                    <i
                      className={
                        isSyncSettingsOpen
                          ? 'pi pi-chevron-up'
                          : 'pi pi-chevron-down'
                      }
                    />
                  </StyledCollapseButton>
                </StyledHeaderActions>
              </StyledCardHeader>

              {isSyncSettingsOpen && (
                <Formik
                  enableReinitialize
                  initialValues={savedValues}
                  onSubmit={(values) => {
                    setSavedValues(values);
                  }}
                  innerRef={fieldMappingFormikRef}
                >
                  {({ values, setFieldValue }) => (
                    <StyledForm className="w-full">
                      <FormDirtyTracker onDirtyChange={setFieldMappingDirty} />
                      <StyledHeaderRow>
                        <div>
                          {t(
                            'azure.entra.fieldMapping.column.cyberpilotProperty',
                          )}
                        </div>
                        <div>
                          {t(
                            'azure.entra.fieldMapping.column.entraIdAttribute',
                          )}
                        </div>
                        <div>{t('azure.entra.fieldMapping.column.status')}</div>
                      </StyledHeaderRow>

                      {flatRows.map((row) => {
                        const isLocked = !!row.locked;
                        const enabledPath = `${row.key}.enabled`;
                        const attributePath = `${row.key}.attribute`;
                        const isSynced = isLocked || values[row.key].enabled;
                        const attributeText = values[row.key].attribute;

                        return (
                          <StyledRow key={row.key} isLocked={isLocked}>
                            <StyledFieldLabel $locked={isLocked}>
                              {isLocked ? (
                                <FlexContainer
                                  id={`locked-${row.key}`}
                                  justify="flex-start"
                                  align="center"
                                  gap="var(--xsmall-padding)"
                                >
                                  <SimpleLockIcon />
                                  <span>{row.label}</span>
                                  <Tooltip
                                    target={`#locked-${row.key}`}
                                    content={t(
                                      'azure.entra.fieldMapping.lockedFieldsNotice',
                                    )}
                                    position="left"
                                    style={{ width: '230px' }}
                                  />
                                </FlexContainer>
                              ) : (
                                <span>{row.label}</span>
                              )}
                            </StyledFieldLabel>

                            <FlexContainer
                              gap={12}
                              justify="flex-start"
                              align="center"
                              wrap="wrap"
                            >
                              {isPropertyEditing ? (
                                isLocked ? (
                                  <StyledPill>
                                    {attributeText ||
                                      t('azure.entra.fieldMapping.notSet')}
                                  </StyledPill>
                                ) : (
                                  <div className="w-full">
                                    <FlexContainer gap={8} align="center">
                                      {(() => {
                                        const savedValueIsCustom =
                                          !!attributeText &&
                                          !attributeOptions.includes(
                                            attributeText as AzureAdField,
                                          );
                                        const showCustomInput =
                                          customDropdownRows[row.key] ||
                                          savedValueIsCustom;
                                        const dropdownValue = showCustomInput
                                          ? CUSTOM_ATTRIBUTE
                                          : attributeText;

                                        return (
                                          <>
                                            <Field
                                              name={attributePath}
                                              component={FormikDropdown}
                                              value={dropdownValue}
                                              options={[
                                                ...attributeOptions.map(
                                                  (option) => ({
                                                    label: option,
                                                    value: option,
                                                  }),
                                                ),
                                                {
                                                  label: t(
                                                    'azure.entra.fieldMapping.customAttribute',
                                                  ),
                                                  value: CUSTOM_ATTRIBUTE,
                                                },
                                              ]}
                                              className="white w-full"
                                              placeholder={t(
                                                'azure.entra.fieldMapping.selectPlaceholder',
                                              )}
                                              valueTemplate={
                                                attributeValueTemplate
                                              }
                                              itemTemplate={
                                                attributeItemTemplate
                                              }
                                              onChange={(e: any) => {
                                                const nextValue = e.value;
                                                if (
                                                  nextValue === CUSTOM_ATTRIBUTE
                                                ) {
                                                  setCustomDropdownRows(
                                                    (prev) => ({
                                                      ...prev,
                                                      [row.key]: true,
                                                    }),
                                                  );
                                                  setFieldValue(
                                                    attributePath,
                                                    '',
                                                  );
                                                } else {
                                                  setCustomDropdownRows(
                                                    (prev) => ({
                                                      ...prev,
                                                      [row.key]: false,
                                                    }),
                                                  );
                                                  setFieldValue(
                                                    attributePath,
                                                    nextValue,
                                                  );
                                                }
                                              }}
                                            />
                                            {showCustomInput && (
                                              <InputText
                                                value={attributeText || ''}
                                                onChange={(e) =>
                                                  setFieldValue(
                                                    attributePath,
                                                    e.target.value,
                                                  )
                                                }
                                                placeholder={t(
                                                  'azure.entra.fieldMapping.customAttributePlaceholder',
                                                )}
                                                className="white w-full"
                                              />
                                            )}
                                          </>
                                        );
                                      })()}
                                    </FlexContainer>
                                  </div>
                                )
                              ) : (
                                <StyledPill>
                                  {attributeText ||
                                    t('azure.entra.fieldMapping.notSet')}
                                </StyledPill>
                              )}
                            </FlexContainer>

                            <FlexContainer justify="flex-start" align="center">
                              {isPropertyEditing ? (
                                <InputSwitch
                                  checked={values[row.key].enabled}
                                  onChange={(e: any) =>
                                    setFieldValue(enabledPath, !!e.value)
                                  }
                                  className="v2 big"
                                  disabled={row.locked}
                                />
                              ) : (
                                <AppChip
                                  type={isSynced ? 'primary' : 'secondary'}
                                  label={
                                    isSynced
                                      ? t('azure.entra.fieldMapping.synced')
                                      : t('azure.entra.fieldMapping.notSynced')
                                  }
                                  size="s"
                                />
                              )}
                            </FlexContainer>
                          </StyledRow>
                        );
                      })}
                    </StyledForm>
                  )}
                </Formik>
              )}
            </StyledSyncSettingsInner>
          </StyledSyncSettingsPanel>
        )}

        <StyledSyncSettingsPanel>
          <StyledSyncSettingsInner>
            <StyledCardHeader>
              <div>
                <StyledTitle>
                  {t('azure.entra.adGroupMapping.title')}
                </StyledTitle>
                <StyledSubtitle>
                  {t('azure.entra.adGroupMapping.description')}
                </StyledSubtitle>
              </div>
              <StyledCollapseButton
                type="button"
                aria-label={t('azure.entra.adGroupMapping.toggleCollapse')}
                onClick={() => setIsAdGroupMappingOpen((v) => !v)}
              >
                <i
                  className={
                    isAdGroupMappingOpen
                      ? 'pi pi-chevron-up'
                      : 'pi pi-chevron-down'
                  }
                />
              </StyledCollapseButton>
            </StyledCardHeader>

            {isAdGroupMappingOpen && (
              <Formik
                enableReinitialize
                initialValues={savedAdGroupValues}
                onSubmit={(values) => {
                  setSavedAdGroupValues(values);
                }}
                innerRef={adGroupFormikRef}
              >
                {({
                  values,
                  setFieldValue,
                  errors,
                  touched,
                  setFieldTouched,
                }) => (
                  <Form className="w-full">
                    <FormDirtyTracker onDirtyChange={setAdGroupDirty} />
                    <StyledCheckboxesContainer className="toggle-container">
                      <FlexContainer justify="space-between" align="center">
                        <div>
                          <StyledCardRowTitle>
                            {t('azure.entra.adGroupMapping.enable')}
                          </StyledCardRowTitle>
                        </div>
                        <AppSwitch
                          checked={!!values.active}
                          onChange={(e: any) =>
                            setFieldValue('active', !!e.value, true)
                          }
                          className="v2"
                          disabled={false}
                        />
                      </FlexContainer>
                    </StyledCheckboxesContainer>

                    {values.active && (
                      <>
                        {Object.keys(values.mapping).map((key) => (
                          <UserManagementGroupSelection
                            title={t('azure.mapGroup', {
                              platformName: account?.platformSettings?.name,
                            })}
                            groupLabel={t('azure.groupId')}
                            key={key}
                            index={key}
                            values={values.mapping}
                            group={values.mapping[key]}
                            account={account}
                            setFieldValue={setFieldValue}
                            errors={errors}
                            touched={touched}
                            setFieldTouched={setFieldTouched}
                            disabled={!values.active}
                          />
                        ))}

                        <StyledFullWidthAddButton
                          type="button"
                          aria-label={t('azure.entra.adGroupMapping.addNew')}
                          onClick={() =>
                            setFieldValue('mapping', {
                              ...values.mapping,
                              [v4()]: {
                                groupId: '',
                                account: account,
                                branches: [],
                                groups: [],
                                language: LanguagesEnum.EN,
                              },
                            })
                          }
                          disabled={!values.active}
                        >
                          <i className="pi pi-plus" />
                          {t('azure.entra.adGroupMapping.addNew')}
                        </StyledFullWidthAddButton>
                        <StyledSyncNowRow className="mt-3">
                          <AppButton
                            icon="pi pi-sync"
                            type="outlined"
                            severity="no-background"
                            label={t('azure.force.sync')}
                            isDisabled={
                              !values.active ||
                              !onSync ||
                              adGroupDirty ||
                              fieldMappingDirty ||
                              userStatusDirty
                            }
                            tooltip={
                              adGroupDirty ||
                              fieldMappingDirty ||
                              userStatusDirty
                                ? t('azure.force.sync.dirty.tooltip')
                                : undefined
                            }
                            tooltipOptions={{
                              position: 'top',
                            }}
                            state={syncState}
                            onClick={onSync}
                          />
                        </StyledSyncNowRow>
                      </>
                    )}
                  </Form>
                )}
              </Formik>
            )}
          </StyledSyncSettingsInner>
        </StyledSyncSettingsPanel>

        <StyledSyncSettingsPanel>
          <StyledSyncSettingsInner>
            <StyledUserStatusSectionHeader>
              <StyledUserStatusTitleRow>
                <StyledUserStatusTitleCol>
                  <StyledTitle>{t('azure.entra.userStatus.title')}</StyledTitle>
                  <StyledUserStatusSectionSubtitle>
                    {t('azure.entra.userStatus.description')}
                  </StyledUserStatusSectionSubtitle>
                </StyledUserStatusTitleCol>
              </StyledUserStatusTitleRow>
            </StyledUserStatusSectionHeader>

            <StyledUserStatusInnerCard>
              <FlexContainer
                justify="flex-start"
                align="center"
                gap={'var(--xsmall-padding)'}
              >
                <StyledCardRowTitle>
                  {t('azure.entra.userStatus.rowTitle')}
                </StyledCardRowTitle>
                <InputSwitch
                  checked={userStatusSyncEnabled}
                  onChange={(e) => setUserStatusSyncEnabled(!!e.value)}
                  className="v2 big"
                />
              </FlexContainer>
              <StyledUserStatusDescription>
                {userStatusSyncEnabled ? (
                  <>
                    <StyledUserStatusDescriptionLine>
                      {t(
                        isUserStatusLockEnabled
                          ? 'azure.entra.userStatus.rowDescriptionOnLine1'
                          : 'azure.entra.userStatus.rowDescriptionOnUnlockedLine1',
                      )}
                    </StyledUserStatusDescriptionLine>
                    <StyledUserStatusDescriptionLine>
                      {isUserStatusLockEnabled ? (
                        <Trans
                          i18nKey="azure.entra.userStatus.rowDescriptionOnLine2"
                          components={{ bold: <strong /> }}
                        />
                      ) : (
                        t(
                          'azure.entra.userStatus.rowDescriptionOnUnlockedLine2',
                        )
                      )}
                    </StyledUserStatusDescriptionLine>
                  </>
                ) : (
                  <>
                    <StyledUserStatusDescriptionLine>
                      {t('azure.entra.userStatus.rowDescriptionOffLine1')}
                    </StyledUserStatusDescriptionLine>
                    <StyledUserStatusDescriptionLine>
                      {t('azure.entra.userStatus.rowDescriptionOffLine2')}
                    </StyledUserStatusDescriptionLine>
                    <StyledUserStatusDescriptionLine>
                      {t('azure.entra.userStatus.rowDescriptionOffLine3')}
                    </StyledUserStatusDescriptionLine>
                  </>
                )}
              </StyledUserStatusDescription>
            </StyledUserStatusInnerCard>
          </StyledSyncSettingsInner>
        </StyledSyncSettingsPanel>
      </StyledTable>
    </FormContainer>
  );
};
